import { useCallback } from 'react';
import useDropdowns from './useDropdowns';
import { type ActiveProjectsDropdownCategory, getActiveProjectsDropdowns } from '@/config/activeProjectsDropdownConfig';

interface UseActiveProjectsDropdownsOptions {
  categories?: ActiveProjectsDropdownCategory[];
  autoLoad?: boolean;
}

interface ActiveProjectsDropdownResult {
  data: Record<string, any>;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  
  // Helper functions for Active Projects dropdowns
  getOptionByValue: (category: string, value: string) => any;
  getOptionByLabel: (category: string, label: string) => any;
  searchOptions: (category: string, searchTerm: string) => any[];
}

export const useActiveProjectsDropdowns = ({ 
  categories, 
  autoLoad = true 
}: UseActiveProjectsDropdownsOptions = {}): ActiveProjectsDropdownResult => {

  // Create service method for active projects using direct data access (bypassing complex service layer)
  const serviceMethod = useCallback(async () => {
    try {
      // Use direct data access instead of the service layer to avoid import path issues
      const data = getActiveProjectsDropdowns(categories);
      return {
        success: true,
        data: data,
        message: 'Successfully loaded dropdown options'
      };
    } catch (error) {
      console.error('❌ useActiveProjectsDropdowns: Error loading data:', error);
      throw error;
    }
  }, []); // Remove categories dependency to prevent recreation

  // Now gets all functionality including helper functions from useDropdowns
  const result = useDropdowns({
    module: 'active-projects',
    categories,
    autoLoad,
    serviceMethod
  });

  // Simply return everything from useDropdowns (now includes helper functions)
  return result;
};

export default useActiveProjectsDropdowns;

